import { useState } from "react"

import { LogOut, Share2, UserPlus } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { SavedDashboard } from "../services/dashboard-service"

import type { DirectorySearchResult } from "@/services/directory-search-service"
import type { ParticipantRole } from "@/services/project-service"

import { DirectorySearch } from "@/components/directory-search"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { ParticipantList } from "@/features/projects/components/participant-list"

import { SettingsService } from "@/services/settings-service"

import {
  useAddDashboardParticipant,
  useDashboardParticipants,
  useRemoveDashboardParticipant,
  useUpdateDashboardParticipantRole,
} from "../hooks/use-dashboard-participants"

type DashboardShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  dashboard: SavedDashboard | undefined
  onLeave?: () => void
}

// Owner role is owner-of-record only — assigned at create time. Transfer
// is deferred to fas 2 because the dashboards container partitions on
// /owner_id, which would require moving the doc between partitions.
type AddableRole = "reader" | "editor" | "admin"

export function DashboardShareDialog({
  open,
  onOpenChange,
  dashboard,
  onLeave,
}: DashboardShareDialogProps) {
  const { t } = useTranslation()
  const [newParticipantRole, setNewParticipantRole] = useState<AddableRole>("reader")
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)

  const dashboardId = dashboard?.id
  const currentUserOid = SettingsService.getInstance().claims.oid

  const { data: participantsData, isLoading } = useDashboardParticipants(dashboardId)

  const addParticipant = useAddDashboardParticipant()
  const updateParticipantRole = useUpdateDashboardParticipantRole()
  const removeParticipant = useRemoveDashboardParticipant()

  const canManage = dashboard?.currentUserRole === "owner" || dashboard?.currentUserRole === "admin"

  const isDirectParticipant =
    (participantsData?.participants ?? []).some(
      (p) => p.oid === currentUserOid && p.type === "user"
    ) && dashboard?.currentUserRole !== "owner"
  const canLeave = isDirectParticipant

  const existingOids = (participantsData?.participants.map((p) => p.oid) ?? []).filter(
    Boolean
  ) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (!dashboardId) return
    if (existingOids.includes(result.oid)) return
    await addParticipant.mutateAsync({
      dashboardId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: newParticipantRole,
      },
    })
  }

  const handleRoleChange = async (participantOid: string, role: ParticipantRole) => {
    if (!dashboardId) return
    await updateParticipantRole.mutateAsync({ dashboardId, participantOid, role })
  }

  const handleRemove = async (participantOid: string) => {
    if (!dashboardId) return
    await removeParticipant.mutateAsync({ dashboardId, participantOid })
  }

  const handleConfirmLeave = async () => {
    if (!dashboardId || !currentUserOid) return
    try {
      await removeParticipant.mutateAsync({
        dashboardId,
        participantOid: currentUserOid,
      })
      setShowLeaveDialog(false)
      onOpenChange(false)
      onLeave?.()
    } catch {
      // Handled by mutation onError
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="size-5" />
              {t("dash.sharing.dialog_title", { defaultValue: "Share dashboard" })}
            </DialogTitle>
            <DialogDescription>
              {t("dash.sharing.dialog_description", {
                defaultValue: "Add users or groups to collaborate on this dashboard.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {canManage && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="text-muted-foreground size-4" />
                  <Label className="text-sm font-medium">
                    {t("dash.sharing.add_participant", { defaultValue: "Add people" })}
                  </Label>
                </div>

                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <DirectorySearch
                      disabled={addParticipant.isPending}
                      selectedOids={existingOids}
                      onSelect={handleAddParticipant}
                    />
                  </div>
                  <Select
                    value={newParticipantRole}
                    onValueChange={(v) => setNewParticipantRole(v as AddableRole)}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reader">
                        {t("dash.sharing.role_reader", { defaultValue: "Reader" })}
                      </SelectItem>
                      <SelectItem value="editor">
                        {t("dash.sharing.role_editor", { defaultValue: "Editor" })}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t("dash.sharing.role_admin", { defaultValue: "Admin" })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t("dash.sharing.participants", { defaultValue: "People with access" })}
              </Label>
              <ParticipantList
                canManage={canManage}
                currentUserOid={currentUserOid}
                emptyParticipantsText={t("dash.sharing.no_participants", {
                  defaultValue: "Not shared yet. Add users or groups to invite them.",
                })}
                entityType="project"
                isCurrentUserOwner={dashboard?.currentUserRole === "owner"}
                isLoading={isLoading}
                ownerUserId={participantsData?.ownerUserId}
                participants={participantsData?.participants ?? []}
                onRemove={handleRemove}
                onRoleChange={handleRoleChange}
              />
            </div>

            <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
              <p className="mb-1 font-medium">
                {t("dash.sharing.role_descriptions_title", {
                  defaultValue: "Permission levels:",
                })}
              </p>
              <ul className="space-y-0.5">
                <li>
                  <strong>{t("dash.sharing.role_reader", { defaultValue: "Reader" })}:</strong>{" "}
                  {t("dash.sharing.role_reader_desc", {
                    defaultValue: "Can view the dashboard and refresh its data",
                  })}
                </li>
                <li>
                  <strong>{t("dash.sharing.role_editor", { defaultValue: "Editor" })}:</strong>{" "}
                  {t("dash.sharing.role_editor_desc", {
                    defaultValue: "Can also edit tiles and save changes",
                  })}
                </li>
                <li>
                  <strong>{t("dash.sharing.role_admin", { defaultValue: "Admin" })}:</strong>{" "}
                  {t("dash.sharing.role_admin_desc", {
                    defaultValue: "Can also manage participants",
                  })}
                </li>
                <li>
                  <strong>{t("dash.sharing.role_owner", { defaultValue: "Owner" })}:</strong>{" "}
                  {t("dash.sharing.role_owner_desc", {
                    defaultValue: "Full control. Owner transfer ships in fas 2.",
                  })}
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {canLeave ? (
              <Button
                className="text-destructive hover:text-destructive gap-2"
                disabled={removeParticipant.isPending}
                variant="ghost"
                onClick={() => setShowLeaveDialog(true)}
              >
                <LogOut className="size-4" />
                {t("dash.sharing.leave", { defaultValue: "Leave dashboard" })}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("dash.sharing.leave_title", { defaultValue: "Leave dashboard?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("dash.sharing.leave_description", {
                defaultValue:
                  "You will lose access to this dashboard. The owner can invite you again to restore access.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction
              disabled={removeParticipant.isPending}
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmLeave()
              }}
            >
              {t("dash.sharing.leave", { defaultValue: "Leave dashboard" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
